import { useCallback, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Play, ChevronDown, ChevronRight, Check } from "lucide-react";
import { toast } from "sonner";
import { IssueList } from "@/components/integrity/IssueList";

const API = "/api";

function Stat({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function StudentRow({ s, onApply, busy }) {
  const [open, setOpen] = useState(false);
  const diff = s.discrepancy;
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`credit-row-${s.student_id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 min-w-[200px] text-left" data-testid={`credit-row-toggle-${s.student_id}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-medium text-slate-900">{s.name}</span>
          <span className="text-xs text-slate-400">{s.issues.length} finding(s)</span>
        </button>
        <div className="text-sm text-slate-600 w-28">Current: <strong data-testid={`credit-current-${s.student_id}`}>{s.current_balance}</strong></div>
        <div className="text-sm text-slate-600 w-28">Correct: <strong data-testid={`credit-expected-${s.student_id}`}>{s.expected_balance}</strong></div>
        <div className={`text-sm font-semibold w-24 ${diff === 0 ? "text-slate-400" : diff > 0 ? "text-amber-700" : "text-rose-700"}`}>
          {diff === 0 ? "No change" : diff > 0 ? `${diff} too many` : `${-diff} too few`}
        </div>
        <div className="text-xs text-slate-500 w-40">Reserved: {s.reserved_upcoming} upcoming{s.reserved_past_untagged ? ` + ${s.reserved_past_untagged} past untagged` : ""}</div>
        {s.needs_correction ? (
          <Button size="sm" disabled={busy} onClick={() => onApply(s)} data-testid={`credit-apply-${s.student_id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply correction
          </Button>
        ) : <span className="text-xs text-slate-400 w-32 text-right">Review only</span>}
      </div>
      {open && (
        <div className="px-10 pb-4">
          <p className="text-xs text-slate-500 mb-2">
            Purchased {s.total_purchased}{s.credit_adjustment ? ` + manual adjustment ${s.credit_adjustment}` : ""} − {s.consumed_records} consumed classes.
            Available for new bookings after fix: <strong>{s.available_after_fix}</strong>
          </p>
          <IssueList issues={s.issues} testid={`credit-issues-${s.student_id}`} />
        </div>
      )}
    </div>
  );
}

export function CreditAuditPanel() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/integrity/credits`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const apply = async (s) => {
    if (!window.confirm(`Apply correction for ${s.name}?\n\nBalance: ${s.current_balance} → ${s.expected_balance}\n\nThe change will be logged with your name.`)) return;
    setBusyId(s.student_id);
    try {
      const res = await axios.post(`${API}/admin/integrity/credits/${s.student_id}/apply`, { expected_balance: s.expected_balance }, { headers: getAuthHeader() });
      toast.success(`${s.name}: balance ${res.data.correction.original_value} → ${res.data.correction.corrected_value}`);
      await run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction failed");
    } finally {
      setBusyId(null);
    }
  };

  const needing = data?.students.filter((s) => s.needs_correction) || [];
  const reviewOnly = data?.students.filter((s) => !s.needs_correction) || [];

  return (
    <div className="space-y-4 mt-4" data-testid="credit-audit-panel">
      <div className="flex items-center gap-3">
        <Button onClick={run} disabled={loading} data-testid="run-credit-audit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run credit audit
        </Button>
        {data && <span className="text-xs text-slate-400">Last run {new Date(data.generated_at).toLocaleString()}</span>}
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Students scanned" value={data.scanned_students} testid="credit-scanned" />
            <Stat label="Need correction" value={data.students_needing_correction} tone="text-rose-700" testid="credit-needing" />
            <Stat label="With findings" value={data.students_with_issues} tone="text-amber-700" testid="credit-with-issues" />
            <Stat label="Net credit difference" value={data.total_credit_discrepancy} testid="credit-net-diff" />
          </div>
          {data.duplicate_student_names.length > 0 && (
            <p className="text-sm text-amber-700" data-testid="duplicate-names-warning">
              Duplicate student names (bookings by name cannot be auto-linked): {data.duplicate_student_names.join(", ")}
            </p>
          )}
          <Card><CardContent className="p-0">
            <div className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-50 border-b">Balances that need correction ({needing.length})</div>
            {needing.length ? needing.map((s) => <StudentRow key={s.student_id} s={s} onApply={apply} busy={busyId === s.student_id} />)
              : <p className="p-4 text-sm text-emerald-700" data-testid="credit-all-clean">All balances match the class records.</p>}
          </CardContent></Card>
          {reviewOnly.length > 0 && (
            <Card><CardContent className="p-0">
              <div className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-50 border-b">Balance correct — items to review ({reviewOnly.length})</div>
              {reviewOnly.map((s) => <StudentRow key={s.student_id} s={s} onApply={apply} busy={false} />)}
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}
